/**
 * Custom-domain rules for the admin UI.
 *
 * Normalisation, apex/www handling and the DNS-record builder are re-exported
 * from the edge functions' runtime-agnostic helper so the browser, the edge
 * functions and (via the SQL mirror) the database all agree on what a domain
 * is. That file has no imports, so it bundles cleanly into the app.
 */
import {
  buildDnsInstructions,
  canonicaliseCustomDomain,
  DEFAULT_APEX_A,
  DEFAULT_CNAME,
  normaliseDomain,
  stageFor,
  type DnsRecord,
  type DomainStage,
  type VercelChallenge,
} from '../../supabase/functions/_shared/custom-domain';

export {
  buildDnsInstructions,
  canonicaliseCustomDomain,
  DEFAULT_APEX_A,
  DEFAULT_CNAME,
  normaliseDomain,
  stageFor,
  type DnsRecord,
  type DomainStage,
  type VercelChallenge,
};

/** Friendly reason a typed domain was rejected (null when it's fine). */
export function domainInputError(raw: string): string | null {
  const c = canonicaliseCustomDomain(raw);
  if (c.ok === true) return null;
  const reason = 'reason' in c ? c.reason : 'invalid';
  if (reason === 'empty') return 'Enter the domain you want to use.';
  if (reason === 'platform') return 'Your thepolishedversion.com address is already set up — enter a domain you own.';
  return "That doesn't look like a domain name. Try something like yourbusiness.co.uk";
}

export interface StageCopy {
  label: string;
  tone: 'amber' | 'blue' | 'green' | 'rose' | 'violet';
  title: string;
  body: string;
}

export function stageCopy(stage: DomainStage, domain: string, opts: { reason?: string | null; includeWww?: boolean } = {}): StageCopy {
  switch (stage) {
    case 'live':
      return {
        label: 'Live',
        tone: 'green',
        title: 'Your domain is live',
        body: opts.includeWww
          ? `Customers can now find you at https://${domain}. www.${domain} forwards there automatically.`
          : `Customers can now find you at https://${domain}.`,
      };
    case 'waiting_ssl':
      return {
        label: 'Waiting for SSL',
        tone: 'blue',
        title: 'Setting up your secure padlock',
        body: 'Your DNS is pointing at us. We’re now issuing your free SSL certificate — this usually takes a few minutes.',
      };
    case 'setup_pending':
      return {
        label: 'Almost there',
        tone: 'violet',
        title: 'Finishing setup on our side',
        body: 'Your domain is verified. Our team needs to finish connecting it — you don’t need to do anything. If it’s not live within a day, please get in touch.',
      };
    case 'problem':
      return {
        label: 'Problem',
        tone: 'rose',
        title: 'Something needs sorting',
        body: 'We hit a problem connecting your domain. The details are below.',
      };
    case 'checking_dns':
    default:
      if (opts.reason === 'vercel_challenge') {
        return {
          label: 'Checking DNS',
          tone: 'amber',
          title: 'One more record needed',
          body: 'This domain has been used with our hosting provider before, so they need one extra TXT record (marked “Extra” below) to confirm it’s yours.',
        };
      }
      if (opts.reason === 'dns_not_pointing') {
        return {
          label: 'Checking DNS',
          tone: 'amber',
          title: 'Ownership confirmed — now point your domain at us',
          body: 'We found your TXT record. Next, make sure the A and CNAME records below are in place. Changes can take up to 24 hours to show up.',
        };
      }
      return {
        label: 'Checking DNS',
        tone: 'amber',
        title: 'Waiting for your DNS records',
        body: 'Add the records below where you manage your domain. Changes can take up to 24 hours to show up — we’ll keep checking while this page is open, and in the background.',
      };
  }
}
